import { api, PartipostApiResponse, PartipostApiPaginationWrapper } from '.'
import { FilterParameters, Objective } from 'state/campaignTemplates'
import {
    Country,
    PLATFORM_CONTENT_TYPE_ENUM,
    PlatformContentType,
    STEP_TYPE_ENUM,
    TASK_TYPE_ENUM,
} from 'services/api/constants'
import { MediaData } from 'components/MediaViewer'
import { TextListGroupValue } from 'particles/TextInputs/TextList/Group'
import _ from 'lodash'
import { RichInfoListGroupValue } from 'particles/TextInputs/TextList/RichInfoListGroup'
import { ElementKeys } from 'components/Campaign/Template/TemplateDetail/options'
export interface CampaignTemplatesResult {
    uuid: string
    country: Country
    name: string
    objective: string
    description: string
    kpi_range: {
        min: string
        max: string
    }
    duration: string
}

type TaskFieldName =
    | 'products'
    | 'min_purchase_quantity'
    | 'example_medias'
    | 'example_media_description'
    | 'purchase_locations'
    | 'brand_handles'
    | 'caption_hashtags'
    | 'unique_selling_point'
    | 'review_platform'
    | 'brand_name'
    | 'follow_platform'

// todo: add more StepData based on Task type
export interface PlaceholderKeyData {
    // Purchase step placeholder key
    title?: string
    how_to_buy?: string[]
    product_name?: string
    example_photos?: string[]
    // Submit Proof Photo step placeholder key
    things_to_note?: string[]
    media_requirement_dos?: string[]
    media_requirement_donts?: string[]
    media_requirement_tags?: string[]
    caption_requirement_dos?: string[]
    caption_requirement_donts?: string[]
    caption_requirement_hashtags?: string[]
    submission_requirement_description?: string
    // Tiktok repost step placeholder key
    blocks?: {
        title: string
        description: string
        type: string
        max_photos?: number
    }[]
}

export interface StepData {
    data: PlaceholderKeyData
    type: string
    schedule_setting: {
        start_time: {
            offset: number
        }
        end_time: {
            offset: number
        }
    }
}

interface ElementData {
    type: string
    steps: StepData[]
    title: string
    payouts: {
        // todo needs to udpate if backend have more sub fields in payouts
        base: {
            cost: number
        }
    }
    description: string
}

interface TaskElement {
    key: string
    uuid: string
    placeholder_keys: TaskFieldName[]
    data: ElementData
    platform_content_type: PLATFORM_CONTENT_TYPE_ENUM
}

export interface TemplateDetail {
    uuid: string
    key: string // Cannot define a strict type here because the key combination can include any task type, ex: purchase:ig_reels or ig_reels:purchase
    country: Country
    objective: Objective
    kpi_range: {
        min: number
        max: number
    }
    task_elements: TaskElement[]
}

export interface CampaignTemplatesRequest extends FilterParameters {
    url?: string
}

export type CampaignTemplatesResponse = PartipostApiPaginationWrapper<CampaignTemplatesResult[]>

export const getCampaignTemplates = (payload: CampaignTemplatesRequest) => {
    const { url, ...rest } = payload
    const baseUrl = url ? url.replace('http:', 'https:') : 'campaign-templates/'
    const params = {
        ...rest,
    }
    return api.get<PartipostApiResponse<CampaignTemplatesResponse>>(baseUrl, params)
}

export interface PlatformContentTypeProps {
    platform_content_type: PLATFORM_CONTENT_TYPE_ENUM
    create_content_required: boolean | null
    approval_for_review_text_required: boolean | null
    image_for_review_text_required: boolean | null
}

export interface SaveTemplateFiltersRequest {
    template: {
        uuid?: string | null
    }
    brand: {
        uuid?: string | null
    }
    objective?: Objective | null
    get_product_required?: boolean | null
    platform_content_types?: PlatformContentTypeProps[]
    budget?: number | null
    // average_product_price: number //todo: enable this in flow 2b
}

export interface TemplateData extends SaveTemplateFiltersRequest {
    uuid: string
    campaign: {
        uuid: string
    }
}

export const saveTemplateFiltersAndCreateCampaign = (payload: SaveTemplateFiltersRequest) => {
    return api.post<PartipostApiResponse<TemplateData>>('campaign-template-datas/', payload)
}

export interface UpdateTemplateDataRequest {
    templateDataUuid: string
    brandUuid?: string | null
    budget?: number | null
    showSuccessToast?: boolean
}

export const updateTemplateData = (payload: UpdateTemplateDataRequest) => {
    const { templateDataUuid } = payload
    const requestPayload: Record<string, number | { uuid: string | undefined }> = {}
    if (payload.brandUuid) {
        requestPayload['brand'] = { uuid: payload.brandUuid }
    }
    if (payload.budget) {
        requestPayload['budget'] = payload.budget
    }
    return api.patch<PartipostApiResponse<TemplateData>>(`campaign-template-datas/${templateDataUuid}/`, requestPayload)
}

export interface TemplateDetailRequest {
    templateUuid: string
    setNavigationDisable?: (value: boolean) => void
    showSuccessToast?: boolean
}

export const getTemplateDetail = (payload: TemplateDetailRequest) => {
    const { templateUuid } = payload
    return api.get<PartipostApiResponse<TemplateDetail>>(`campaign-templates/${templateUuid}/`)
}

export interface TemplateDataRequest {
    templateDataUuid: string
}

export const getTemplateData = (payload: TemplateDataRequest) => {
    const { templateDataUuid } = payload
    return api.get<PartipostApiResponse<TemplateData>>(`campaign-template-datas/${templateDataUuid}/`)
}

export interface SwitchTemplateRequest {
    templateDataUuid: string
    templateUuid: string
}

export interface SwitchTemplateResponse {
    template: {
        uuid: string
    }
}

export const switchTemplate = (payload: SwitchTemplateRequest) => {
    const { templateDataUuid, templateUuid } = payload
    return api.patch<PartipostApiResponse<SwitchTemplateResponse>>(
        `campaign-template-datas/${templateDataUuid}/template/`,
        { template: { uuid: templateUuid } },
    )
}

export interface CampaignInfoRequest extends TemplateDataRequest {}

export interface CampaignInfo {
    title: string
    categories: number[]
    images: MediaData[]
    registration_enable: boolean
    use_default_image: boolean
}

export const getCampaignInfo = (payload: CampaignInfoRequest) => {
    const { templateDataUuid } = payload
    return api.get<PartipostApiResponse<CampaignInfo>>(`campaign-template-datas/${templateDataUuid}/campaign-info/`)
}

export interface UpdateCampaignInfo extends TemplateDataRequest {
    title?: string
    categories?: number[]
    images?: MediaData[]
    registration_enable?: boolean
    setNavigationDisable?: (value: boolean) => void
    showSuccessToast?: boolean
}

export const updateCampaignInfo = (payload: CampaignInfoRequest) => {
    const { templateDataUuid, ...parameters } = payload
    const noneNullParameters = _.omitBy(parameters, _.isNil)
    return api.patch<PartipostApiResponse<CampaignInfo>>(
        `campaign-template-datas/${templateDataUuid}/campaign-info/`,
        noneNullParameters,
    )
}

interface TimeWindow {
    start: string
    end: string
}

export interface StepSchedule {
    title: string
    time_window: TimeWindow
    type: STEP_TYPE_ENUM
}

export interface TaskSchedule {
    title: string
    steps: StepSchedule[]
    type: TASK_TYPE_ENUM
}

export interface CampaignScheduleRequest extends TemplateDataRequest {
    start_time: string | null
    payout_deadline: string | null
}

export interface CampaignSchedule {
    start_time: string
    campaign_time_window: TimeWindow
    tasks: TaskSchedule[]
    registration_time_window?: TimeWindow
    payout_deadline: string
}

export const getCampaignSchedule = (payload: CampaignScheduleRequest) => {
    const { templateDataUuid, start_time } = payload
    return api.get<PartipostApiResponse<CampaignSchedule>>(
        `campaign-template-datas/${templateDataUuid}/campaign-schedule`,
        { start_time },
    )
}

export interface UpdateCampaignSchedule extends TemplateDataRequest {
    start_time: string | null
    payout_deadline: string | null
    setNavigationDisable?: (value: boolean) => void
    showSuccessToast?: boolean
    skipLoading?: boolean
}

export const updateCampaignSchedule = (payload: CampaignScheduleRequest) => {
    const { templateDataUuid, ...parameters } = payload
    return api.patch<PartipostApiResponse<CampaignSchedule>>(
        `campaign-template-datas/${templateDataUuid}/campaign-schedule/`,
        parameters,
    )
}

export interface CampaignTaskElementsRequest extends TemplateDataRequest {}

interface ElementId {
    uuid: string
}
export interface CampaignTaskElements {
    element: ElementId
    example_medias?: MediaData[]
    products?: {
        uuid: string
        value: {
            name: string
            price: number
        }
    }[]
    example_media_description?: string
    min_purchase_quantity?: number
    purchase_locations?: TextListGroupValue[]
    brand_handles?: TextListGroupValue[]
    caption_hashtags?: TextListGroupValue[]
    unique_selling_point?: TextListGroupValue[]
    brand_name?: string

    app_name?: string
    review_platform?: string
    review_link?: string
    follow_platform?: string
    follow_platform_link?: string
    download_links?: {
        uuid: string
        value: {
            platform: string
            link: number
        }
    }[]
    sample_links?: TextListGroupValue[]
    sticker_link?: string
    customized_sticker_text?: string
    customized_sticker_text_toggle?: boolean
    items_to_review?: string[]
    shipping_fee?: number
    number_of_videos?: number
    mandatory_guidelines?: string
}

export const getCampaignTaskElements = (payload: CampaignTaskElementsRequest) => {
    const { templateDataUuid } = payload
    return api.get<PartipostApiResponse<CampaignTaskElements[]>>(
        `campaign-template-datas/${templateDataUuid}/task-element-data/`,
    )
}

export type UpdateCampaignTaskElementsRequest = TemplateDetailRequest & CampaignTaskElements

export const updateCampaignTaskElements = (payload: UpdateCampaignTaskElementsRequest) => {
    const { templateUuid, ...restPayload } = payload
    const textListKeys: (keyof CampaignTaskElements)[] = [
        'brand_handles',
        'caption_hashtags',
        'unique_selling_point',
        'purchase_locations',
        'sample_links',
    ]
    const richInfoListKeys: (keyof CampaignTaskElements)[] = ['products']
    const mediaUpload: (keyof CampaignTaskElements)[] = ['example_medias']

    const objKeys = Object.keys(restPayload) as (keyof CampaignTaskElements)[]

    // check array object value property, if all is "" then convert into empty array []
    // remove empty list item
    const newPayload = objKeys.reduce((acc, curr) => {
        const value = restPayload[curr]
        const isArray = Array.isArray(value)
        let isArrayEmpty = true
        const notEmptyValue: (TextListGroupValue | RichInfoListGroupValue | MediaData)[] = []

        if (textListKeys.includes(curr) && isArray) {
            value.forEach(v => {
                if (!_.isEmpty((v as TextListGroupValue)?.value)) {
                    isArrayEmpty = false
                    notEmptyValue.push(v as TextListGroupValue)
                }
            })
        } else if (curr === 'download_links' && isArray) {
            isArrayEmpty = false
            value.forEach(v => {
                notEmptyValue.push(v as RichInfoListGroupValue)
            })
        } else if (richInfoListKeys.includes(curr) && isArray) {
            value.forEach(v => {
                const productValue = {
                    ...(v as RichInfoListGroupValue),
                } as RichInfoListGroupValue
                if (!_.isEmpty(productValue?.value?.['name']) || !_.isEmpty(productValue?.value?.['price'])) {
                    isArrayEmpty = false
                    if (productValue?.value.price === '0') {
                        productValue.value.price = ''
                    }
                    notEmptyValue.push(productValue)
                }
            })
        } else if (mediaUpload.includes(curr) && isArray) {
            isArrayEmpty = false
            notEmptyValue.push(...(value as MediaData[]))
        }

        const payloadValue = isArray ? (isArrayEmpty ? [] : notEmptyValue) : value

        return {
            ...acc,
            [curr]: payloadValue,
        }
    }, {} as CampaignTaskElements)

    // since product will be [] if no value at all, then min_purchase_quantity should also adjusted to 1
    if (newPayload['products']?.length === 0) {
        newPayload['min_purchase_quantity'] = 1
    }
    return api.patch<PartipostApiResponse<CampaignTaskElements[]>>(
        `campaign-template-datas/${templateUuid}/task-element-data/`,
        newPayload,
    )
}

export const submitCampaignTemplate = (payload: TemplateDetailRequest) => {
    const { templateUuid } = payload
    return api.put<PartipostApiResponse<CampaignTaskElements[]>>(
        `campaign-template-datas/${templateUuid}/save-and-submit/`,
    )
}

export interface TemplateRequest {
    templateUuid: string
}

export interface TemplatePaymentSummaryData {
    budget: number
    influencer_budget: number
    slot_number: number
    total_payout: number
    breakdown?: {
        title: string
        element_key: ElementKeys
        platform_content_type: PlatformContentType
        payout: number
        min_purchase_quantity: number | null
        shipping_fee: number | null
    }[]
    left_over_budget: number
    campaign_title: string
    template_name: string
    partipost_fee: number
    additional_fees?: {
        uuid: string
        title: string
        amount: number
    }[]
    service_fee_percentage: number
    subtotal: number
    detailed_mode: boolean
    tax: {
        amount: number
        label: string
        percentage: number
    }
    payable_amount: number //this is campaign_payment
}

export interface GetPaymentSummaryPayload extends TemplateRequest {
    budget?: number
}

export const getPaymentSummary = (payload: GetPaymentSummaryPayload) => {
    const { templateUuid, budget } = payload
    return api.get<PartipostApiResponse<TemplatePaymentSummaryData>>(
        `campaign-template-datas/${templateUuid}/payment-summary/`,
        { budget },
    )
}

export interface UpdatePaymentSummaryPayload extends TemplateRequest {
    budget: number
}

export const updatePaymentSummary = (payload: UpdatePaymentSummaryPayload) => {
    const { templateUuid, budget } = payload
    return api.patch<PartipostApiResponse<TemplatePaymentSummaryData>>(
        `campaign-template-datas/${templateUuid}/payment-summary/`,
        {
            budget,
        },
    )
}

export const sendToEdit = (payload: TemplateRequest) => {
    const { templateUuid } = payload
    return api.put<PartipostApiResponse<object>>(`campaign-template-datas/${templateUuid}/save-and-request-edit/`)
}

export const requestPayment = (payload: TemplateRequest) => {
    const { templateUuid } = payload
    return api.put<PartipostApiResponse<object>>(`campaign-template-datas/${templateUuid}/request-payment/`)
}

export const sendToReview = (payload: TemplateRequest) => {
    const { templateUuid } = payload
    return api.put<PartipostApiResponse<object>>(`campaign-template-datas/${templateUuid}/send-back-to-for-review/`)
}

export const publishTemplate = (payload: TemplateRequest) => {
    const { templateUuid } = payload
    return api.put<PartipostApiResponse<object>>(`campaign-template-datas/${templateUuid}/save-and-publish/`)
}

export const exportTemplate = (payload: TemplateRequest) => {
    const { templateUuid } = payload
    return api.put<PartipostApiResponse<object>>(`campaign-template-datas/${templateUuid}/export-to-editor/`)
}
